import { useState } from 'react';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import {
  Container,
  Stack,
  Typography,
  TextField,
  Button,
  Grid,
  styled,
  Autocomplete,
  DialogContent,
  DialogContentText,
  Dialog,
  DialogActions
} from '@mui/material';

import { Box } from '@mui/system';
import { format } from 'date-fns';
import { BiCloudUpload, BiCloudDownload } from 'react-icons/bi';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ExcelRenderer } from 'react-excel-renderer';
import { saveApiData } from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Label from '../../components/Label';
import PageTitle from '../../components/PageHeading';

import { UserListHead, UserListToolbar, UserMoreMenu } from '../../components/_dashboard/user';
import RevenueService from '../services/RevenueService';
import Loader from '../Global/Loader';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';
import { PayoutApi } from '../../utils/Apis';
import { savefileApiData } from '../../store/Slice/Payout/PayoutSlice';

const { DownloadPayoutTemplate } = RevenueService;

/**
 * Represents a book.
 * @constructor
 * @param {string} title - The title of the book.
 * @param {string} author - The author of the book.
 */

function UploadBulkFilePayout() {
  const navigate = useNavigate();
  const { rowId } = useParams();

  const [downloadTemplateMsg, setDownloadTemplateMsg] = useState('');
  const [openDownloadTemplateDialog, setOpenDownloadTemplateDialog] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [uploadSummary, setUploadSummary] = useState(null);
  const [openSummaryDialog, setOpenSummaryDialog] = useState(false);

  const [file, setFile] = useState();

  const Input = styled('input')({
    display: 'none'
  });
  const [open, setOpen] = useState(false);

  const [fileDetails, setFileDetails] = useState({
    BankName: null,
    FileType: ''
  });

  const [uploadfile, setUploadfile] = useState(null);

  const { LoginData } = useLoginSelector();
  const { BankDrop } = useGlobalSelector();

  const handleBankNameChange = (newValue) => {
    setFileDetails((prevData) => ({
      ...prevData,
      BankName: newValue
    }));
  };
  const dispatch = useDispatch();

  const handleRenderFile = (fileObj) => {
    ExcelRenderer(fileObj, (err, resp) => {
      if (err) {
        console.error('Error reading excel file', err);
      }
      // If you need to process `resp.rows`, do it here…
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const fileName = selectedFile.name;
    const ext = fileName.split('.').pop().toLowerCase();

    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setUploadfile(null);
      setSelectedFileName('');
      setOpen(true);
      dispatch(setApiError('Please upload only xlsx, xls or csv file'));
      return;
    }

    setUploadfile(selectedFile);
    setSelectedFileName(fileName);

    // Optional preview/validation
    handleRenderFile(selectedFile);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await DownloadPayoutTemplate(LoginData?.jwt);

      const contentType = response.headers.get('content-type');

      const blob = await response.blob();
      let fileName = `Partner_Payout_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}`;
      let mimeType = contentType;

      fileName += '.xlsx';
      mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(setApiError('Template Successfully Downloaded'));
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const bankId = fileDetails.BankName?.value;

    if (!bankId) {
      setOpen(true);
      dispatch(setApiError('Please select bank name'));
      return;
    }

    if (!uploadfile) {
      setOpen(true);
      dispatch(setApiError('Please upload MIS file'));
      return;
    }

    try {
      const formUploadData = new FormData();
      formUploadData.append('file', uploadfile);
      formUploadData.append('bankId', String(bankId));
      formUploadData.append('userId', String(LoginData?.userId));

      const result = await dispatch(savefileApiData(PayoutApi?.payoutUpload, formUploadData));

      // console.log('Upload result:', result);

      if (result?.data?.status === 'success') {
        setUploadSummary(result?.data);
        setOpenSummaryDialog(true);
      } else {
        dispatch(setApiError(result?.message || 'Upload failed'));
      }
    } catch (error) {
      console.error(error);
      dispatch(setApiError('Upload failed due to server error'));
    }
  };
  function handleUploadInfo() {
    setOpenSummaryDialog(false);
    navigate('/payout');
  }

  return (
    <Page title="Partner Management System || Revenue">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Upload Files" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            File Details
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Bank Name
                  </Typography>
                  <Autocomplete
                    disablePortal
                    id="combo-box-bank"
                    options={BankDrop || []}
                    size="small"
                    getOptionLabel={(option) => option?.label || ''}
                    renderOption={(props, option) => <li {...props}>{option?.label}</li>}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth placeholder="Select Bank" />
                    )}
                    value={fileDetails.BankName || null}
                    onChange={(e, newValue) => handleBankNameChange(newValue)}
                  />
                </Box>
              </Grid>

              <Grid item xs="10" mb={3}>
                <Box variant="Container" className="uploadBox">
                  <label htmlFor="contained-button-file">
                    <input
                      accept=".xlsx,.xls,.csv"
                      className="fileInput"
                      id="contained-button-file"
                      type="file"
                      onChange={handleFileChange}
                    />
                    <Box>
                      <BiCloudUpload className="upladIcon" />
                      <Typography variant="h5">
                        {selectedFileName ? selectedFileName : 'Upload'}
                      </Typography>
                    </Box>
                  </label>
                </Box>
              </Grid>
              <Grid xs="2" item mb={3} sx={{ flexGrow: '1' }}>
                <Button
                  className="mainBtn downloadBtn"
                  variant="contained"
                  onClick={handleDownloadTemplate}
                >
                  <BiCloudDownload className="downloadIcon" />
                  <Typography variant="subtitle1">
                    Download <br />
                    Template
                  </Typography>
                </Button>
              </Grid>
            </Grid>
            <Box className="" textAlign="right">
              <Button
                type="button"
                variant="contained"
                className="mainBtn"
                onClick={(e) => handleSubmit(e)}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
        <Dialog
          open={openSummaryDialog}
          onClose={() => setOpenSummaryDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {uploadSummary?.message}
            </Typography>
            <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
              <strong>Total Records: {uploadSummary?.summary?.processedCount}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Successfully Uploaded: {uploadSummary?.summary?.successfulCount}</strong>
            </Typography>
            <Typography variant="body2" color="error">
              <strong>Failed Records: {uploadSummary?.summary?.failedCount}</strong>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleUploadInfo()} variant="contained" className="mainBtn">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
export default UploadBulkFilePayout;
